import { useState, useCallback } from "react";

/**
 * A hook that mimics useMMKVObject but handles QuotaExceededError gracefully.
 * If the data is too large for LocalStorage/MMKV, it falls back to React State (RAM).
 */
export const useAdaptiveMMKV = (key, storage, initialValue = null) => {
  // 1. Initialize State
  const [value, setValue] = useState(() => {
    try {
      const storedString = storage.getString(key);
      return storedString ? JSON.parse(storedString) : initialValue;
    } catch (error) {
      console.warn(`[AdaptiveMMKV] Failed to load key "${key}". Using initial value.`, error);
      return initialValue;
    }
  });

  // 2. Custom Setter: Defers to React's internal queue to prevent race conditions
  const setAdaptiveValue = useCallback((newValue) => {
    setValue((prevValue) => {
      // Resolve using the absolute latest state provided by React
      const resolvedValue = typeof newValue === 'function' ? newValue(prevValue) : newValue;

      // Sync to storage synchronously alongside the state update
      try {
        if (resolvedValue === undefined || resolvedValue === null) {
          storage.delete(key);
        } else {
          storage.set(key, JSON.stringify(resolvedValue));
        }
      } catch (error) {
        console.warn(
          `[AdaptiveMMKV] Storage quota exceeded for key "${key}". Data is preserved in RAM but will not persist on reload.`
        );
      }

      // Return the new state to React
      return resolvedValue;
    });
  // 'value' is removed from dependencies. This setter is now permanently stable.
  }, [key, storage]);

  return [value, setAdaptiveValue];
};